import type { PlayerSeasonStatRow } from '../../../../services/player/playerSeasonStats'
import { resolveAssetUrl } from '../../../../utils/assetUrl'
import type { ImageLike } from '../../../../utils/assetUrl'
import { MISSING_STAT } from '../constants'
import type { GameStat, SeasonGridRow, StatColumn } from '../types'
import { buildGameResult, formatMatchup } from './gameResult'
import { formatGameDate } from './recentGameCard'
import { sortSeasonRowsNewestFirst } from './seasonColumns'
import { getGameStatValue, getStatValue } from './statValues'

interface BuildSeasonGridRowsArgs {
  seasons: PlayerSeasonStatRow[]
  columns: StatColumn[]
  /** The expanded season row's id (team + season). */
  openSeasonRowId: string | null
  /** Games of the open row's schedule — every team the player was on. */
  openGames: GameStat[] | undefined
  openGamesError: Error | null
  timezone: string
}

interface TeamInfo {
  id: string | undefined
  name: string
  image: ImageLike | undefined
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

const isImageLike = (value: unknown): value is ImageLike =>
  typeof value === 'string' || isRecord(value)

const readTeam = (team: unknown): TeamInfo => {
  if (!isRecord(team)) {
    return { id: undefined, name: '', image: undefined }
  }
  return {
    id: asString(team.id),
    name: asString(team.name_full) ?? asString(team.name) ?? '',
    image: isImageLike(team.image) ? team.image : undefined,
  }
}

const buildValues = (
  stats: PlayerSeasonStatRow['stats'],
  columns: StatColumn[]
): Record<string, string> =>
  Object.fromEntries(
    columns.map(({ abbr }) => [abbr, getStatValue(stats, abbr) ?? MISSING_STAT])
  )

const listAbbrs = (stats: PlayerSeasonStatRow['stats']): string[] =>
  (stats ?? []).flatMap((s) => {
    const abbr = s.abbr ?? s.type?.abbr
    return abbr ? [abbr] : []
  })

/** Game rows read omitted count stats as 0, but only for stats the season tracks. */
const buildGameValues = (
  stats: GameStat['stats'],
  columns: StatColumn[],
  trackedAbbrs: string[]
): Record<string, string> =>
  Object.fromEntries(
    columns.map(({ abbr }) => [
      abbr,
      getGameStatValue(stats, abbr, trackedAbbrs) ?? MISSING_STAT,
    ])
  )

/** Date → matchup → result; `label` joins them for the hover title. */
const buildGameRow = (
  g: GameStat,
  seasonRowId: string,
  columns: StatColumn[],
  trackedAbbrs: string[],
  timezone: string
): SeasonGridRow => {
  const date = formatGameDate(g.game.starts_at, timezone, 'MMM D')
  const matchup = formatMatchup(g)
  const result = buildGameResult(g)
  return {
    kind: 'game',
    id: `${seasonRowId}-${g.game.id}`,
    gameId: g.game.id,
    date,
    matchup,
    result,
    label: [date, matchup, result.label].join(' · '),
    values: buildGameValues(g.stats, columns, trackedAbbrs),
  }
}

/** Team + season: one schedule can hold several of the player's teams. */
const getSeasonRowId = (
  row: PlayerSeasonStatRow,
  index: number,
  teamId: string | undefined
): string => `${teamId ?? 'team'}-${row.season?.id ?? index}`

/** The schedule whose games the open season row needs, or null when none is open. */
export const getOpenScheduleId = (
  seasons: PlayerSeasonStatRow[],
  openSeasonRowId: string | null
): string | null => {
  if (openSeasonRowId === null) {
    return null
  }
  const open = sortSeasonRowsNewestFirst(seasons).find(
    (row, index) =>
      getSeasonRowId(row, index, readTeam(row.team).id) === openSeasonRowId
  )
  return open?.season?.id || null
}

export const buildSeasonGridRows = ({
  seasons,
  columns,
  openSeasonRowId,
  openGames,
  openGamesError,
  timezone,
}: BuildSeasonGridRowsArgs): SeasonGridRow[] =>
  sortSeasonRowsNewestFirst(seasons).flatMap((row, index) => {
    const team = readTeam(row.team)
    const seasonId = row.season?.id ?? ''
    const id = getSeasonRowId(row, index, team.id)
    const isOpen = seasonId !== '' && id === openSeasonRowId

    const seasonRow: SeasonGridRow = {
      kind: 'season',
      id,
      seasonId,
      teamId: team.id,
      teamName: team.name,
      teamLogo: team.image ? resolveAssetUrl(team.image) : undefined,
      seasonName: row.season?.name ?? '',
      values: buildValues(row.stats, columns),
      isOpen,
    }
    if (!isOpen) {
      return [seasonRow]
    }
    if (openGames === undefined) {
      return openGamesError
        ? [seasonRow, { kind: 'error', id: `${id}-error` }]
        : [seasonRow, { kind: 'loading', id: `${id}-loading` }]
    }
    const trackedAbbrs = listAbbrs(row.stats)
    const teamGames =
      team.id === undefined
        ? openGames
        : openGames.filter((g) => g.team.id === team.id)
    return [
      seasonRow,
      ...teamGames.map((g) =>
        buildGameRow(g, id, columns, trackedAbbrs, timezone)
      ),
    ]
  })
